import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { ScoringRule } from '@/types/scoring';
import { DEFAULT_MLB_TRASH_SCORING } from '@/data/mlbScoringData';
import { supabase } from '@/integrations/supabase/client';

interface ScoringRulesContextType {
  scoringRules: ScoringRule[];
  setScoringRules: (rules: ScoringRule[] | ((prev: ScoringRule[]) => ScoringRule[])) => void;
  loading: boolean;
}

const ScoringRulesContext = createContext<ScoringRulesContextType>({
  scoringRules: DEFAULT_MLB_TRASH_SCORING,
  setScoringRules: () => {},
  loading: true,
});

export const useScoringRules = () => useContext(ScoringRulesContext);

interface Props {
  leagueId: string | null;
  children: ReactNode;
}

const normalizeMlbScoringRules = (rules: ScoringRule[]): ScoringRule[] => {
  const hasLegacyOutRules = rules.some((r) => r.id === 'bat_go' || r.id === 'bat_ao' || r.stat === 'groundouts' || r.stat === 'flyouts');
  if (!hasLegacyOutRules) return rules;

  const cleaned = rules.filter((r) => r.id !== 'bat_go' && r.id !== 'bat_ao' && r.stat !== 'groundouts' && r.stat !== 'flyouts');

  const existingOutRule = cleaned.find((r) => r.id === 'bat_outs' || r.stat === 'outs');
  if (existingOutRule) return cleaned;

  const legacyCandidates = rules.filter((r) => r.id === 'bat_go' || r.id === 'bat_ao' || r.stat === 'groundouts' || r.stat === 'flyouts');
  const firstEnabled = legacyCandidates.find((r) => r.isEnabled);
  const pointSource = legacyCandidates.find((r) => typeof r.pointValue === 'number');

  const outRule: ScoringRule = {
    id: 'bat_outs',
    category: 'batting',
    stat: 'outs',
    label: 'Out (Any Type)',
    description: 'Points per out recorded (at-bats minus hits)',
    pointValue: pointSource?.pointValue ?? 0.5,
    isNegative: false,
    isEnabled: firstEnabled ? true : false,
  };

  return [...cleaned, outRule];
};

export const ScoringRulesProvider = ({ leagueId, children }: Props) => {
  const [scoringRules, setScoringRulesState] = useState<ScoringRule[]>(DEFAULT_MLB_TRASH_SCORING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) {
      setLoading(false);
      return;
    }

    const loadRules = async () => {
      const { data } = await supabase
        .from('leagues')
        .select('category_config')
        .eq('id', leagueId)
        .single();

      if (data?.category_config) {
        try {
          const config = data.category_config as any;
          if (Array.isArray(config?.scoring_rules) && config.scoring_rules.length > 0) {
            setScoringRulesState(normalizeMlbScoringRules(config.scoring_rules));
          }
        } catch {
          // Use defaults
        }
      }
      setLoading(false);
    };

    loadRules();
  }, [leagueId]);

  const setScoringRules = useCallback((rules: ScoringRule[] | ((prev: ScoringRule[]) => ScoringRule[])) => {
    setScoringRulesState((prev) => {
      const next = typeof rules === 'function' ? rules(prev) : rules;
      return normalizeMlbScoringRules(next);
    });
  }, []);

  return (
    <ScoringRulesContext.Provider value={{ scoringRules, setScoringRules, loading }}>
      {children}
    </ScoringRulesContext.Provider>
  );
};
